/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { LegendItem } from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendItem';
import {
    ComparisonItem,
    LegendLabel,
    Row,
    StatsText
} from 'src/luis/projects/analytics/components/TrainingEvaluation/Legend/LegendRowHelpers';

export type StatsLegendProps = {
    /**
     * Flag to indicate if the legend is in comparison mode or not.
     */
    inComparisonMode: boolean;

    /**
     * The number of intents.
     */
    numIntents: number;

    /**
     * The number of entities.
     */
    numEntities: number;

    /**
     * The number of utterances.
     */
    numUtterances: number;

    /**
     * The difference between the numbers of intents of the current version and the compared version.
     */
    numIntentsDiff?: number;

    /**
     * The difference between the numbers of entities of the current version and the compared version.
     */
    numEntitiesDiff?: number;

    /**
     * The difference between the numbers of utterances of the current version and the compared version.
     */
    numUtterancesDiff?: number;
};

const messages = defineMessages({
    intentsLabel: {
        id: 'analytics.statsLegend.intentsLabel',
        defaultMessage: 'Intents'
    },
    entitiesLabel: {
        id: 'analytics.statsLegend.entitiesLabel',
        defaultMessage: 'Entities'
    },
    utterancesLabel: {
        id: 'analytics.statsLegend.utterancesLabel',
        defaultMessage: 'Utterances'
    }
});

const legendTheme = getScopedTheme('Dashboard');

const { styleComponent } = getStylist('Dashboard');
const Label = styleComponent(LegendLabel)('Label', theme => ({ fontSize: theme.labelFontSize }));

/**
 * The legend row to represent statistics numbers not percentages like:
 * number of intents, entities, utterances ...etc.
 *
 * @param props the properties of the row.
 */
const StatsLegendRow = (props: { value: number; label: string; loadingDiff: boolean; diff: number }) => {
    const { label, value, diff, loadingDiff } = props;

    return (
        <Row>
            <LegendItem color="none" />
            <StatsText customProps={{ font: legendTheme.mediumFontSize, lineHeight: legendTheme.largeLineHeight }}>{value}</StatsText>
            <ComparisonItem customProps={{ rowType: 'stats' }}>
                {loadingDiff ? (diff !== null ? `${diff < 0 ? '' : '+'}${diff}` : '--') : ''}
            </ComparisonItem>
            <Label>{label}</Label>
        </Row>
    );
};

/**
 * The legend items that show the number of intents, entities and utterance.
 *
 * @param props The number of intents, entities and utterance and the differences if exist.
 */
export const StatsLegend = (props: StatsLegendProps) => {
    const { numIntents, numIntentsDiff, numEntities, numEntitiesDiff, numUtterances, numUtterancesDiff, inComparisonMode } = props;
    const { localize } = useLocalization();

    return (
        <>
            <StatsLegendRow
                loadingDiff={inComparisonMode}
                value={numIntents}
                label={localize(messages.intentsLabel)}
                diff={numIntentsDiff}
            />
            <StatsLegendRow
                loadingDiff={inComparisonMode}
                value={numEntities}
                label={localize(messages.entitiesLabel)}
                diff={numEntitiesDiff}
            />
            <StatsLegendRow
                loadingDiff={inComparisonMode}
                value={numUtterances}
                label={localize(messages.utterancesLabel)}
                diff={numUtterancesDiff}
            />
        </>
    );
};
